import { motion, useReducedMotion } from 'framer-motion';
import { type ReactNode } from 'react';

interface FadeInProps {
  children: ReactNode;
  delay?: number;
  duration?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  distance?: number;
  className?: string;
  once?: boolean;
}

const directionOffsets = {
  up: { y: 1 },
  down: { y: -1 },
  left: { x: 1 },
  right: { x: -1 },
  none: {},
};

export function FadeIn({
  children,
  delay = 0,
  duration = 0.5,
  direction = 'up',
  distance = 24,
  className = '',
  once = true,
}: FadeInProps) {
  const shouldReduceMotion = useReducedMotion();

  const offset = directionOffsets[direction];
  const initialX = 'x' in offset ? offset.x * distance : 0;
  const initialY = 'y' in offset ? offset.y * distance : 0;

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: initialX, y: initialY }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, margin: '-50px' }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function FadeInOnScroll({
  children,
  delay = 0,
  duration = 0.6,
  direction = 'up',
  distance = 30,
  className = '',
}: Omit<FadeInProps, 'once'>) {
  return (
    <FadeIn
      delay={delay}
      duration={duration}
      direction={direction}
      distance={distance}
      className={className}
      once
    >
      {children}
    </FadeIn>
  );
}

export default FadeIn;
